import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Breadcrumbs,
    InputAdornment,
    Link,
    Pagination,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import {Search} from "@mui/icons-material";
import useApiCall, {makeApiCall} from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../../components/LoadingContent";
import {Division} from "../../data/Division";
import {LimitedResourceResponse, ResourcePaginatedResponse} from "../../data/LimitedResourceResponse";
import {Link as RouterLink, useParams} from "react-router-dom";
import useUser from "../../hooks/useUser";
import {GlobalHiddenDivisions} from "../../utils";
import {ResourceDivisionSidebar} from "../../components/resource/ResourceDivisionSidebar";
import {ResourceContentPanel} from "../../components/resource/ResourceContentPanel";

interface ResourcePageContentProps {
    divisions: Division[];
}

/** Matches a resource against a search query by title or type */
function resourceMatchesQuery(resource: LimitedResourceResponse, query: string): boolean {
    if (resource.title.toLowerCase().includes(query)) return true;
    const typeLabel = resource.type === "content" ? "document" : resource.type.toLowerCase();
    return resource.type.toLowerCase().includes(query) || typeLabel.includes(query);
}

function ResourcePageContent({divisions}: ResourcePageContentProps) {
    const theme = useTheme();
    const user = useUser();
    const params = useParams();

    const [searchQuery, setSearchQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const [page, setPage] = useState(1);

    // Backend-paginated global view state
    const [globalResources, setGlobalResources] = useState<LimitedResourceResponse[]>([]);
    const [globalTotalPages, setGlobalTotalPages] = useState(1);
    const [globalTotalCount, setGlobalTotalCount] = useState(0);
    const [globalLoading, setGlobalLoading] = useState(true);

    // Per-division resources (fetched when a division is selected)
    const [divisionResources, setDivisionResources] = useState<LimitedResourceResponse[]>([]);
    const [divisionLoading, setDivisionLoading] = useState(false);

    const visibleDivisions = useMemo(
        () => divisions.filter((d) => !GlobalHiddenDivisions.includes(d.identifier)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [divisions.map((d) => d.identifier).join(",")]
    );

    // Use local state for selection, seeded from URL param on mount
    const [selectedIdentifier, setSelectedIdentifier] = useState<string | null>(
        params.divisionIdentifier?.replaceAll("-", "_") ?? null
    );
    const selectedDivision = visibleDivisions.find((d) => d.identifier === selectedIdentifier) ?? null;

    const isGlobalView = !selectedIdentifier;
    const query = searchQuery.trim().toLowerCase();
    const isSearching = query.length > 0;

    // Fetch paginated global resources from backend
    useEffect(() => {
        if (!isGlobalView) return;
        setGlobalLoading(true);
        const params = new URLSearchParams();
        if (query) params.set("search", query);
        if (typeFilter !== "all") params.set("type", typeFilter);
        const queryString = params.toString() ? `?${params.toString()}` : "";
        const {cancel} = makeApiCall<ResourcePaginatedResponse>({
            url: `/api/resource/all/${page}${queryString}`,
            onLoadedCallback: (data) => {
                setGlobalResources(data.resources);
                setGlobalTotalPages(data.totalPages);
                setGlobalTotalCount(data.totalCount);
                setGlobalLoading(false);
            },
            onError: () => {
                setGlobalLoading(false);
            },
        });
        return () => cancel();
    }, [page, isGlobalView, query, typeFilter]);

    // Fetch resources for the selected division
    useEffect(() => {
        if (!selectedIdentifier) return;
        setDivisionLoading(true);
        const {cancel} = makeApiCall<LimitedResourceResponse[]>({
            url: `/api/resource/division/${selectedIdentifier}`,
            onLoadedCallback: (data) => {
                setDivisionResources(data);
                setDivisionLoading(false);
            },
            onError: () => {
                setDivisionLoading(false);
            },
        });
        return () => cancel();
    }, [selectedIdentifier]);

    // Compute display resources
    const displayResources = useMemo(() => {
        if (isGlobalView) {
            // Backend handles pagination, search, and type filtering
            return globalResources;
        } else if (selectedIdentifier) {
            let resources = divisionResources;
            if (isSearching) {
                resources = resources.filter((r) => resourceMatchesQuery(r, query));
            }
            if (typeFilter !== "all") {
                resources = resources.filter((r) => r.type === typeFilter);
            }
            return resources;
        }
        return [];
    }, [isGlobalView, isSearching, selectedIdentifier, globalResources, divisionResources, query, typeFilter]);

    const contentDivisionIdentifier = selectedIdentifier ?? null;
    const contentDivision = selectedDivision ?? null;

    // Derive which divisions have results when searching globally
    const matchedDivisions = useMemo(() => {
        if (!isGlobalView || !isSearching) return undefined;
        const set = new Set<string>();
        globalResources.forEach((r) => {
            if (r.divisionIdentifier) set.add(r.divisionIdentifier);
        });
        return set;
    }, [isGlobalView, isSearching, globalResources]);

    const isLoading = isGlobalView ? globalLoading : divisionLoading;

    const handleSelectDivision = (identifier: string | null) => {
        setSelectedIdentifier(identifier);
        setPage(1);
        const newPath = identifier
            ? `/resources/${identifier.replaceAll("_", "-")}`
            : "/resources";
        window.history.replaceState(null, "", newPath);
    };

    return (
        <Box>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{mb: 2, fontSize: "0.85rem"}}>
                <Link component={RouterLink} to="/" underline="hover" color="text.secondary">
                    Home
                </Link>
                {selectedDivision ? (
                    <Link
                        underline="hover"
                        color="text.secondary"
                        sx={{cursor: "pointer"}}
                        onClick={() => handleSelectDivision(null)}
                    >
                        Resources
                    </Link>
                ) : (
                    <Typography color="text.primary" fontSize="0.85rem">
                        Resources
                    </Typography>
                )}
                {selectedDivision && (
                    <Typography color="text.primary" fontSize="0.85rem">
                        {selectedDivision.displayName}
                    </Typography>
                )}
            </Breadcrumbs>

            {/* Search bar and count */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 3}}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={selectedIdentifier
                        ? "Search resources in this division..."
                        : "Search all resources across every division..."
                    }
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="disabled"/>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        maxWidth: 600,
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.palette.background.paper,
                        },
                    }}
                />
                {isGlobalView && (
                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace: "nowrap"}}>
                        <strong>{globalTotalCount}</strong> total resources
                    </Typography>
                )}
            </Stack>

            {/* Main layout: sidebar + content */}
            <Box sx={{display: "flex", gap: 3, flexDirection: {xs: "column", md: "row"}}}>
                <ResourceDivisionSidebar
                    divisions={visibleDivisions}
                    selectedIdentifier={selectedIdentifier}
                    matchedDivisions={matchedDivisions}
                    searchQuery={searchQuery}
                    onSelectDivision={handleSelectDivision}
                />

                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    <ResourceContentPanel
                        division={contentDivision}
                        divisionIdentifier={contentDivisionIdentifier}
                        divisions={visibleDivisions}
                        resources={displayResources}
                        isLoading={isLoading}
                        typeFilter={typeFilter}
                        onTypeFilterChange={(filter) => { setTypeFilter(filter); setPage(1); }}
                        searchQuery={searchQuery}
                        user={user}
                        isGlobalSearch={isGlobalView}
                    />
                    {isGlobalView && globalTotalPages > 1 && (
                        <Stack alignItems="center" sx={{mt: 2, mb: 2}}>
                            <Pagination
                                count={globalTotalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                                shape="rounded"
                            />
                        </Stack>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export const ResourcePage = () => {
    const divisions = useApiCall<Division[]>({
        url: `/api/division/list-divisions`,
    });

    return (
        <LoadingContent isLoading={divisions.isLoading || !divisions}>
            <ResourcePageContent divisions={divisions.data!!}/>
        </LoadingContent>
    );
};
